import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { useAuth } from '../context/AuthContext';
import { apiCall } from '../services/api.service';

const SettingsPage = () => {
  const { user, token, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    mobile: user?.mobile || '',
    gender: user?.gender || '1'
  });
  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (activeTab === 'address') {
      loadAddresses();
    } else if (activeTab === 'order') {
      loadOrders();
    }
  }, [activeTab]);

  const loadAddresses = async () => {
    if (!token || !user?.id) return;
    setLoading(true);
    try {
      const result = await apiCall('post', 'v1/address/getUserAddresses', { user_id: user.id }, token);
      if (result.success) {
        setAddresses(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!token || !user?.id) return;
    setLoading(true);
    try {
      const result = await apiCall('post', 'v1/orders/getUserOrders', { user_id: user.id }, token);
      if (result.success) {
        setOrders(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const updateProfile = async () => {
    setLoading(true);
    try {
      const result = await apiCall('post', 'v1/auth/updateProfile', profileData, token);
      if (result.success) {
        updateUser(result.data);
        setMessage({ type: 'success', text: 'Profile updated successfully' });
      } else {
        setMessage({ type: 'error', text: result.message || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ type: 'error', text: 'An error occurred while updating profile' });
    } finally {
      setLoading(false);
    }
  };

  const deleteAddress = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        const result = await apiCall('post', 'v1/address/deleteAddress', { address_id: addressId }, token);
        if (result.success) {
          setAddresses(prev => prev.filter(addr => addr.id !== addressId));
          setMessage({ type: 'success', text: 'Address deleted successfully' });
        } else {
          setMessage({ type: 'error', text: result.message || 'Failed to delete address' });
        }
      } catch (error) {
        console.error('Delete address error:', error);
        setMessage({ type: 'error', text: 'An error occurred while deleting address' });
      }
    }
  };

  return (
    <>
      <Header />
      <main className="main__content_wrapper">
        <div className="container py-5">
          <div className="row">
            <div className="col-md-3">
              <div className="list-group">
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => setActiveTab('profile')}
                >
                  Profile
                </button>
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'order' ? 'active' : ''}`}
                  onClick={() => setActiveTab('order')}
                >
                  Order History
                </button>
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'address' ? 'active' : ''}`}
                  onClick={() => setActiveTab('address')}
                >
                  My Addresses
                </button>
              </div>
            </div>
            <div className="col-md-9">
              {message.text && (
                <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
                  {message.text}
                  <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
                </div>
              )}

              {activeTab === 'profile' && (
                <div>
                  <h3>User Information</h3>
                  <div className="card">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">First Name</label>
                          <input
                            type="text"
                            className="form-control"
                            name="first_name"
                            value={profileData.first_name}
                            onChange={handleProfileChange}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Last Name</label>
                          <input
                            type="text"
                            className="form-control"
                            name="last_name"
                            value={profileData.last_name}
                            onChange={handleProfileChange}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={profileData.email}
                            onChange={handleProfileChange}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Phone Number</label>
                          <input
                            type="text"
                            className="form-control"
                            name="mobile"
                            value={profileData.mobile}
                            onChange={handleProfileChange}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Gender</label>
                          <select
                            className="form-control"
                            name="gender"
                            value={profileData.gender}
                            onChange={handleProfileChange}
                          >
                            <option value="1">Male</option>
                            <option value="0">Female</option>
                            <option value="2">Others</option>
                          </select>
                        </div>
                        <div className="col-12">
                          <button 
                            className="btn btn-primary"
                            onClick={updateProfile}
                            disabled={loading}
                          >
                            {loading ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'order' && (
                <div>
                  <h3>Order History</h3>
                  {loading ? (
                    <LoadingSkeleton type="order" count={3} />
                  ) : orders.length === 0 ? (
                    <p>No orders found.</p>
                  ) : (
                    orders.map(order => (
                      <div key={order.id} className="card mb-3">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h5>Order #{order.id}</h5>
                              <p className="mb-1">Date: {new Date(order.date_time).toLocaleDateString()}</p>
                              <p className="mb-1">Total: ${order.grand_total}</p>
                              <span className="badge bg-success">Delivered</span>
                            </div>
                            <button className="btn btn-outline-primary">View Details</button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'address' && (
                <div>
                  <h3>My Addresses</h3>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <div className="card h-100 border-primary" style={{cursor: 'pointer'}}>
                        <div className="card-body text-center">
                          <i className="fas fa-plus-circle fa-2x text-primary mb-2"></i>
                          <p>Add New Address</p>
                        </div>
                      </div>
                    </div>
                    {loading ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="col-md-4 mb-3">
                          <div className="card h-100">
                            <div className="card-body">
                              <div className="skeleton skeleton-text mb-2" style={{ height: '20px', width: '60%' }}></div>
                              <div className="skeleton skeleton-text mb-1" style={{ height: '16px', width: '80%' }}></div>
                              <div className="skeleton skeleton-text mb-1" style={{ height: '16px', width: '70%' }}></div>
                              <div className="skeleton skeleton-text" style={{ height: '16px', width: '50%' }}></div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : addresses.map(address => (
                      <div key={address.id} className="col-md-4 mb-3">
                        <div className="card h-100">
                          <div className="card-body">
                            <h5 className="card-title">{address.title}</h5>
                            <p className="card-text">
                              {address.house}, {address.landmark}<br/>
                              {address.address}<br/>
                              {address.pincode}
                            </p>
                            <div className="d-flex gap-2">
                              <button className="btn btn-sm btn-outline-primary">Edit</button>
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => deleteAddress(address.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default SettingsPage;